import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, auth, DEFAULT_TENANT_ID } from '../firebase/config';

/**
 * Centralized logging utility for production and development.
 * Automatically suppresses debug logs in production builds while logging error events to Firestore.
 */

const isProd = import.meta.env.PROD;

/**
 * Removes known PII keys from a context object before writing to Firestore (AUD-073).
 * @param {object} ctx
 * @returns {object}
 */
function scrubContext(ctx) {
  if (!ctx || typeof ctx !== 'object' || Array.isArray(ctx)) return ctx;
  const PII_KEYS = ['email', 'phone', 'password', 'token', 'accessToken', 'refreshToken'];
  const scrubbed = { ...ctx };
  for (const key of PII_KEYS) {
    if (key in scrubbed) scrubbed[key] = '[REDACTED]';
  }
  return scrubbed;
}

function extractErrorObject(error, message) {
  if (error instanceof Error) return error;
  if (message instanceof Error) return message;
  return null;
}

let isLoggingInternal = false;
let globalListenersInitialized = false;

// Client-side rate-limiting and deduplication state (Issue #1059)
const DEDUPE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const dedupeMap = new Map();

const SESSION_RATE_WINDOW_MS = 60 * 1000; // 1 minute
const MAX_SESSION_ERRORS_PER_WINDOW = 5;
let sessionErrorTimestamps = [];

function generateErrorSignature(msgString, errObj, context) {
  const name = errObj?.name || 'Error';
  const source = (context && typeof context === 'object') ? (context.source || context.filename || '') : '';
  return `${name}::${msgString}::${source}`;
}

function isDuplicateError(signature) {
  const now = Date.now();
  const lastTime = dedupeMap.get(signature);
  if (lastTime && now - lastTime < DEDUPE_WINDOW_MS) {
    return true;
  }
  dedupeMap.set(signature, now);
  if (dedupeMap.size > 200) {
    for (const [key, ts] of dedupeMap.entries()) {
      if (now - ts >= DEDUPE_WINDOW_MS) {
        dedupeMap.delete(key);
      }
    }
  }
  return false;
}

function isSessionRateLimited() {
  const now = Date.now();
  sessionErrorTimestamps = sessionErrorTimestamps.filter((ts) => now - ts < SESSION_RATE_WINDOW_MS);
  if (sessionErrorTimestamps.length >= MAX_SESSION_ERRORS_PER_WINDOW) {
    return true;
  }
  sessionErrorTimestamps.push(now);
  return false;
}

const FETCH_NETWORK_PATTERNS = ['Failed to fetch', 'NetworkError', 'Load failed', 'client is offline'];

function isNetworkDisconnectError(message, errObj) {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isOffline) return true;
  const targetStr = `${typeof message === 'string' ? message : ''} ${errObj?.message || ''}`;
  return FETCH_NETWORK_PATTERNS.some((pat) => targetStr.includes(pat));
}

function isOfflineCondition(msgString, errObj) {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return isOffline ||
    msgString.includes('client is offline') ||
    Boolean(errObj?.message && errObj.message.includes('client is offline'));
}

function isEnvironmentLoggingDisabled() {
  const isTestEnv = import.meta.env?.MODE === 'test' || globalThis.process?.env?.NODE_ENV === 'test';
  if (isTestEnv && !logger._enableFirestoreInTest) {
    return true;
  }

  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isDevEnv = !isProd || isLocalhost || import.meta.env?.MODE === 'development';
  return Boolean(isDevEnv && !logger._enableFirestoreInDev);
}

function isAuthRateLimitOrBenign(msgString, errObj) {
  const benignAuthCodes = [
    'auth/too-many-requests',
    'auth/quota-exceeded',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request'
  ];
  if (errObj?.code && benignAuthCodes.includes(errObj.code)) return true;
  return (
    msgString.includes('Too many requests') ||
    msgString.includes('auth/too-many-requests') ||
    msgString.includes('auth/quota-exceeded') ||
    msgString.includes('auth/popup-closed-by-user') ||
    msgString.includes('auth/cancelled-popup-request') ||
    Boolean(errObj?.message && (
      errObj.message.includes('Too many requests') ||
      errObj.message.includes('auth/too-many-requests')
    ))
  );
}

function shouldSkipFirestoreLogging(msgString, errObj) {
  return isOfflineCondition(msgString, errObj) ||
    isNetworkDisconnectError(msgString, errObj) ||
    isAuthRateLimitOrBenign(msgString, errObj) ||
    isEnvironmentLoggingDisabled();
}

function resolveErrorMessage(message, errObj) {
  let msgString = typeof message === 'string'
    ? message
    : (errObj?.message || String(message));

  if (typeof message === 'string' && message.trim().endsWith(':') && errObj?.message) {
    msgString = `${message.trim()} ${errObj.message}`.trim();
  }
  return msgString;
}

const RETENTION_DAYS = 14;
const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

function getWindowUrl() {
  return typeof window !== 'undefined' && window.location ? window.location.href.split('?')[0] : null;
}

function getUserAgent() {
  return typeof navigator !== 'undefined' ? navigator.userAgent : null;
}

function getExpiresAtTimestamp() {
  return Timestamp?.fromMillis
    ? Timestamp.fromMillis(Date.now() + RETENTION_MS)
    : new Date(Date.now() + RETENTION_MS);
}

function buildErrorPayload(msgString, errObj, context) {
  const normalizedContext = (typeof context === 'object' && context !== null) ? context : { value: context };

  return {
    message: msgString,
    errorName: errObj?.name || null,
    stack: errObj?.stack || null,
    context: scrubContext(normalizedContext),
    url: getWindowUrl(),
    userAgent: getUserAgent(),
    userId: auth?.currentUser?.uid || null,
    environment: import.meta.env?.MODE || 'production',
    timestamp: serverTimestamp(),
    expiresAt: getExpiresAtTimestamp(),
    tenantId: DEFAULT_TENANT_ID,
  };
}

export const logger = {
  _enableFirestoreInTest: false,
  _enableFirestoreInDev: false,
  _resetRateLimits: () => {
    dedupeMap.clear();
    sessionErrorTimestamps = [];
  },
  debug: (...args) => {
    if (!isProd) {
      console.debug('[DEBUG]', ...args);
    }
  },

  info: (...args) => {
    if (!isProd) {
      console.info('[INFO]', ...args);
    }
  },

  warn: (...args) => {
    if (!isProd) {
      console.warn('[WARN]', ...args);
    }
  },

  critical: (message, error = null, context = {}) => {
    logger.error(message, error, { ...context, severity: 'critical' }, true);
  },

  error: (message, error = null, context = {}, isCritical = false) => {
    console.error('[ERROR]', message, error, context);

    if (isLoggingInternal) return;

    try {
      isLoggingInternal = true;
      const errObj = extractErrorObject(error, message);
      const msgString = resolveErrorMessage(message, errObj);

      if (shouldSkipFirestoreLogging(msgString, errObj)) {
        return;
      }

      if (!isCritical) {
        const signature = generateErrorSignature(msgString, errObj, context);
        if (isDuplicateError(signature)) {
          return;
        }
        if (isSessionRateLimited()) {
          return;
        }
      }

      const payload = buildErrorPayload(msgString, errObj, context);

      addDoc(collection(db, 'system_errors'), payload).catch((err) => {
        if (!isProd) {
          console.warn('[LOGGER] Failed to write system_error to Firestore:', err);
        }
      });
    } catch (e) {
      if (!isProd) {
        console.warn('[LOGGER] Unexpected exception during error logging:', e);
      }
    } finally {
      isLoggingInternal = false;
    }
  },
};

function isExtensionOrigin(str) {
  if (!str || typeof str !== 'string') return false;
  return (
    str.includes('chrome-extension://') ||
    str.includes('moz-extension://') ||
    str.includes('safari-extension://') ||
    str.includes('safari-web-extension://')
  );
}

const CLIPBOARD_PATTERNS = ["Failed to execute 'writeText' on 'Clipboard'", 'Write permission denied'];

function isClipboardError(error, message) {
  if (error?.name === 'NotAllowedError') return true;
  return CLIPBOARD_PATTERNS.some((pat) => message.includes(pat));
}

function isBenignDomReconciliationError(error, reason, message) {
  const isNotFound = error?.name === 'NotFoundError' || reason?.name === 'NotFoundError' || message.includes('NotFoundError');
  return Boolean(isNotFound && message.includes('removeChild') && message.includes('Node'));
}

function isOpaqueScriptError(message, stack, filename) {
  const OPAQUE_MESSAGES = ['Script error.', 'Script error', 'Unhandled Script Error'];
  return OPAQUE_MESSAGES.includes(message) && !stack && !filename;
}

function shouldIgnoreWindowError(event) {
  const error = event.error || null;
  const filename = event.filename || '';
  const stack = error?.stack || '';

  if (isExtensionOrigin(filename) || isExtensionOrigin(stack)) {
    return true;
  }

  const message = event.message || 'Unhandled Script Error';
  if (isOpaqueScriptError(message, stack, filename)) {
    return true;
  }

  if (isClipboardError(error, message)) {
    return true;
  }

  return isBenignDomReconciliationError(error, null, message);
}

function isAbortError(reason, errorObj) {
  return Boolean(
    errorObj?.name === 'AbortError' ||
    reason?.name === 'AbortError' ||
    (typeof reason === 'string' && reason.includes('aborted')) ||
    (errorObj?.message && errorObj.message.includes('aborted'))
  );
}

function shouldIgnoreUnhandledRejection(reason, errorObj, stack, message) {
  if (isAbortError(reason, errorObj)) {
    return true;
  }

  if (
    isExtensionOrigin(stack) ||
    isExtensionOrigin(typeof reason === 'string' ? reason : '') ||
    isExtensionOrigin(reason?.filename)
  ) {
    return true;
  }

  if (isNetworkDisconnectError(message)) {
    return true;
  }

  if (isClipboardError(errorObj || (typeof reason === 'object' ? reason : null), message)) {
    return true;
  }

  return isBenignDomReconciliationError(errorObj, reason, message);
}

/**
 * Attaches global window error and unhandledrejection event listeners.
 * Ensures all uncaught browser errors and unhandled promise rejections are recorded in system_errors.
 */
export function setupGlobalErrorListeners() {
  if (typeof window === 'undefined' || globalListenersInitialized) return;
  globalListenersInitialized = true;

  window.addEventListener('error', (event) => {
    if (shouldIgnoreWindowError(event)) return;

    const error = event.error || null;
    const message = event.message || 'Unhandled Script Error';

    logger.error(`Unhandled Global Error: ${message}`, error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      source: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const errorObj = reason instanceof Error ? reason : null;
    const stack = errorObj?.stack || (typeof reason === 'string' ? reason : (reason?.stack || ''));
    const message = typeof reason === 'string'
      ? reason
      : (errorObj?.message || 'Unhandled Promise Rejection');

    if (shouldIgnoreUnhandledRejection(reason, errorObj, stack, message)) return;

    logger.error(`Unhandled Promise Rejection: ${message}`, errorObj || reason, {
      source: 'window.onunhandledrejection',
      reasonDetail: typeof reason === 'object' && reason !== null ? scrubContext(reason) : reason,
    });
  });
}

export default logger;

